import { BRAND } from '@tarot/shared';
import { crescentPath, starPath } from './mark';

/**
 * ⭐ CI 단일 출처 (src/brand/) — 화면의 모든 브랜드 마크는 여기 컴포넌트만 사용.
 *  - LogoMark : 풀 CI (부채꼴 카드 3장 + 방사 광선 + 별 + 초승달 + 스파클)
 *  - StarMark : 별 모티프만 (카드 뒷면 등 작은 자리)
 *  - BrandLogo: 헤더용 마크 + 워드마크(고운바탕/Cinzel)
 * 파비콘(mark.ts faviconSvg)과 동일 모티프. 색은 currentColor.
 */

export function LogoMark({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinejoin="round"
      strokeLinecap="round"
      className={className}
      aria-hidden
    >
      {/* 방사 광선 */}
      <g opacity="0.4" strokeWidth="0.9">
        <path d="M24 3.5v4M24 40.5v4M5 22h4M39 22h4" />
        <path d="M10.5 8.5l2.6 2.6M34.9 32.9l2.6 2.6M37.5 8.5l-2.6 2.6M13.1 32.9l-2.6 2.6" />
      </g>
      {/* 부채꼴 카드: 뒤 2장 + 앞 1장 */}
      <g transform="rotate(-20 24 34)" opacity="0.75">
        <rect x="17.5" y="9.5" width="13" height="24" rx="2.2" />
      </g>
      <g transform="rotate(20 24 34)" opacity="0.75">
        <rect x="17.5" y="9.5" width="13" height="24" rx="2.2" />
      </g>
      {/* 카드 앞면 바탕 — 테마 토큰(라이트/다크 자동). SVG 속성 대신 style로 넣어야 var()가 확실히 먹음 */}
      <rect
        x="17.5"
        y="7.5"
        width="13"
        height="26"
        rx="2.2"
        style={{ fill: 'var(--color-card-back)' }}
      />
      <rect
        x="20"
        y="10"
        width="8"
        height="21"
        rx="1.3"
        strokeWidth="0.8"
        opacity="0.5"
      />
      {/* 중앙 별 */}
      <path d={starPath(24, 20.5, 5.2)} fill="currentColor" stroke="none" />
      {/* 초승달 + 스파클 */}
      <path d={crescentPath(6.5, 11, 2.6)} fill="currentColor" stroke="none" opacity="0.8" />
      <path d={starPath(41.5, 7.5, 2.6)} fill="currentColor" stroke="none" opacity="0.9" />
      <circle cx="44" cy="14" r="0.9" fill="currentColor" stroke="none" opacity="0.55" />
    </svg>
  );
}

/** 별 모티프만 — 카드 뒷면·작은 장식용 */
export function StarMark({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d={starPath(12, 12.6, 7.5)} />
      <path d={starPath(18.6, 5, 2.4)} opacity="0.85" />
      <circle cx="5.2" cy="6.4" r="0.8" opacity="0.55" />
    </svg>
  );
}

/** 헤더용 로고: 마크 + 국문 워드마크 + 영문 소자 (영문은 국문 폭에 정확히 맞춰 분산 정렬) */
export function BrandLogo() {
  return (
    <span className="flex items-center gap-2">
      <LogoMark className="h-12 w-12 text-gold" />
      {/* 컨테이너 폭은 국문이 결정(w-fit) → 영문은 글자별 justify-between으로 같은 폭에 락업 */}
      <span className="flex w-fit flex-col justify-center leading-none">
        <span className="font-brand text-2xl font-bold tracking-wide text-gold">{BRAND.nameKo}</span>
        <span
          className="font-brand-en mt-1 flex w-full justify-between text-[10px] font-semibold uppercase text-moon-dim"
          aria-label={BRAND.nameEn}
        >
          {BRAND.nameEn.split('').map((ch, i) => (
            <span key={i}>{ch === ' ' ? ' ' : ch}</span>
          ))}
        </span>
      </span>
    </span>
  );
}
